export type DesignScopeResponsibilityEnumValue = {
  id: number;
  name: string;
  description?: string | null;
};

/** One active Responsible Designer option. `id` is what create/update sends. */
export type DesignScopeResponsibilityOption = {
  id: string;
  name: string;
};

/** Seeded project default. New scope items preselect this when it is in the catalog. */
export const DEFAULT_DESIGN_SCOPE_RESPONSIBILITY = "Architect";

export type AddDesignScopeResponsibilityResult = {
  success: boolean;
  name: string;
  id: string;
};

export type AddDesignScopeResponsibility = (
  name: string
) => Promise<AddDesignScopeResponsibilityResult>;

function responsibilityKey(name: string): string {
  return name.trim().toLocaleLowerCase();
}

/** Case-insensitive name match. Returns the catalog's spelling. */
export function findResponsibilityName(
  names: readonly string[],
  query: string
): string | undefined {
  const key = responsibilityKey(query);
  if (!key) return undefined;
  return names.find((name) => responsibilityKey(name) === key);
}

export function sortResponsibilityNames(names: readonly string[]): string[] {
  return [...names].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

/** Active catalog rows, de-duplicated by name and sorted A–Z. First spelling wins. */
export function responsibilitiesFromEnum(
  enumValues: DesignScopeResponsibilityEnumValue[] | null | undefined
): DesignScopeResponsibilityOption[] {
  const options: DesignScopeResponsibilityOption[] = [];
  const seen = new Set<string>();
  for (const item of enumValues ?? []) {
    const name = (item?.name ?? "").trim();
    const key = responsibilityKey(name);
    const id = item?.id;
    if (!key || seen.has(key) || id === undefined || id === null) continue;
    seen.add(key);
    options.push({ id: String(id), name });
  }
  return options.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );
}

/** Active catalog names, de-duplicated and sorted A–Z. */
export function namesFromResponsibilityEnum(
  enumValues: DesignScopeResponsibilityEnumValue[] | null | undefined
): string[] {
  return responsibilitiesFromEnum(enumValues).map((option) => option.name);
}

/** Case-insensitive name match against catalog rows. */
export function findResponsibilityOption(
  options: readonly DesignScopeResponsibilityOption[],
  query: string
): DesignScopeResponsibilityOption | undefined {
  const key = responsibilityKey(query);
  if (!key) return undefined;
  return options.find((option) => responsibilityKey(option.name) === key);
}

export function defaultResponsibilityId(
  options: readonly DesignScopeResponsibilityOption[]
): string {
  return (
    findResponsibilityOption(options, DEFAULT_DESIGN_SCOPE_RESPONSIBILITY)
      ?.id ?? ""
  );
}

/**
 * Catalog names plus a saved value that is not in the catalog yet, A–Z.
 * The extra value stays visible while editing; it is not written to the catalog.
 */
export function responsibilityOptionNames(
  catalog: readonly string[],
  current: string
): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string) => {
    const name = raw.trim();
    const key = responsibilityKey(name);
    if (!key || seen.has(key)) return;
    seen.add(key);
    names.push(name);
  };
  catalog.forEach(push);
  push(current);
  return sortResponsibilityNames(names);
}

export function defaultResponsibilityName(names: readonly string[]): string {
  return (
    findResponsibilityName(names, DEFAULT_DESIGN_SCOPE_RESPONSIBILITY) ?? ""
  );
}
